import { AccClaimFundingRequest } from '@principle-theorem/principle-core';
import {
  AccAddressType,
  AccBooleanValue,
  IAccClaim,
  IAccClaimDeclaration,
  IAccClaimFormMetadata,
  IAccClaimVendor,
  IAccDentalDiagnosis,
  IAccEmployment,
  IAccEmploymentForm,
  IAccInjury,
  IAccPatient,
  IAccProvider,
  IAccWorkCapacity,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import { omitEmptyValues, WithRef } from '@principle-theorem/shared';
import { omit, pick } from 'lodash';
import {
  IAccAssistanceForm,
  IAccInjuryForm,
} from '../components/acc/acc-claim-lodgement-form/form-groups/acc-injury.formgroup';
import { IAccPatientReferenceForm } from '../components/acc/acc-claim-lodgement-form/form-groups/acc-patient-referance.formgroup';
import { IAccPatientForm } from '../components/acc/acc-claim-lodgement-form/form-groups/acc-patient.formgroup';
import { IAccReferralManagementForm } from '../components/acc/acc-claim-lodgement-form/form-groups/acc-referrals.formgroup';

export interface IAccLodgementFormData {
  patientReference: IAccPatientReferenceForm;
  patient: IAccPatientForm;
  injury: IAccInjuryForm;
  assistance: IAccAssistanceForm;
  employment: IAccEmploymentForm;
  dentalDiagnosis: IAccDentalDiagnosis;
  workCapacity: IAccWorkCapacity;
  referrals: IAccReferralManagementForm;
  declaration: IAccClaimDeclaration;
}

export class AccClaimBuilder {
  private static readonly _pmsSoftwareName = 'Principle';
  private static readonly _practiceNameMaxLength = 40;

  static buildClaimData(
    appVersion: string,
    practice: WithRef<IPractice>,
    data: IAccLodgementFormData
  ): IAccClaim {
    const claimData = {
      pmsSoftwareName: AccClaimBuilder._pmsSoftwareName,
      pmsSoftwareVersion: appVersion,
      claimNumber: data.patientReference.claimNumber,
      vendor: AccClaimBuilder.buildVendor(practice),
      provider: AccClaimBuilder.buildProvider(practice, data.patientReference),
      patient: AccClaimBuilder.buildPatient(data.patient, data.employment),
      employment: AccClaimBuilder.buildEmployment(data.employment),
      injury: AccClaimBuilder.buildInjury(
        data.injury,
        data.assistance,
        data.employment
      ),
      dentalDiagnosis: data.dentalDiagnosis,
      workCapacity:
        data.employment.inPaidEmployment === AccBooleanValue.True
          ? data.workCapacity
          : undefined,
      referrals: data.referrals.referrals,
      declaration: data.declaration,
    };

    return omitEmptyValues(claimData) as IAccClaim;
  }

  static buildMetadata(
    employment: IAccEmploymentForm,
    referrals: IAccReferralManagementForm,
    metadataOverrides?: Partial<IAccClaimFormMetadata>
  ): IAccClaimFormMetadata {
    return {
      occupationCode: employment.occupationCode,
      includeReferrals: referrals.includeReferrals,
      ...metadataOverrides,
    };
  }

  static buildVendor(practice: WithRef<IPractice>): IAccClaimVendor {
    return {
      practiceName: practice.name.slice(
        0,
        AccClaimBuilder._practiceNameMaxLength
      ),
      hpiOrganisationNumber: practice.accSettings?.vendorId ?? '',
      hpiFacilityNumber: practice.accSettings?.facilityId ?? '',
    };
  }

  static buildProvider(
    practice: WithRef<IPractice>,
    patientReference: IAccPatientReferenceForm
  ): IAccProvider {
    return {
      address: AccClaimFundingRequest.formatAddress(
        AccAddressType.Postal,
        practice.metadata?.address?.components
      ),
      details: patientReference.provider,
    };
  }

  static buildPatient(
    patient: IAccPatientForm,
    employment: IAccEmploymentForm
  ): IAccPatient {
    return {
      ...patient,
      occupationCode: employment.occupationCode?.value,
    };
  }

  static buildEmployment(
    employment: IAccEmploymentForm
  ): IAccEmployment | undefined {
    if (!employment.inPaidEmployment) {
      return;
    }
    if (employment.inPaidEmployment === AccBooleanValue.False) {
      return pick(employment, ['inPaidEmployment']);
    }
    return omit(employment, ['occupationCode', 'workInjury']);
  }

  static buildInjury(
    injury: IAccInjuryForm,
    assistance: IAccAssistanceForm,
    employment: IAccEmploymentForm
  ): IAccInjury {
    return {
      ...injury,
      ...assistance,
      workInjury: employment.workInjury,
    };
  }
}
